import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ServiceField {
  id: string;
  field_key: string;
  label: string;
  is_required: boolean;
  input_type: string;
  options: any;
  help_text?: string;
  validation_rules: any;
  sort_order: number;
}

interface UseServiceFieldsReturn {
  fields: ServiceField[];
  loading: boolean;
  error: string | null;
  serviceTypes: string[];
}

export const useServiceFields = (customerId: string | null): UseServiceFieldsReturn => {
  const [fields, setFields] = useState<ServiceField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);

  useEffect(() => {
    if (!customerId) {
      setFields([]);
      setServiceTypes([]);
      setError(null);
      return;
    }

    const fetchServiceFields = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get customer's service types
        const { data: customerServices, error: serviceError } = await supabase
          .from('customer_service_types')
          .select('service_type')
          .eq('customer_id', customerId);

        if (serviceError) throw serviceError;

        // Default to electric if no service types found
        const types = customerServices?.length > 0 
          ? customerServices.map(s => s.service_type)
          : ['electric'];
        
        setServiceTypes(types);

        // Get required fields for these service types
        const { data: serviceFields, error: fieldsError } = await supabase
          .from('service_required_fields')
          .select('*')
          .in('service_type', types)
          .order('sort_order, field_key');

        if (fieldsError) throw fieldsError;

        setFields(serviceFields || []);
      } catch (err) {
        console.error('Error fetching service fields:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch service fields');
        setFields([]);
        setServiceTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceFields();
  }, [customerId]);

  return { fields, loading, error, serviceTypes };
};